import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    EventWrapper,
    EventEachWay,
    SpecialEventsHeaderMaxbetText,
    EventName,
    EventInfo,
    ListWrapper,
    RaceBox,
    ShowMoreWrapper,
    ShowMoreArrowWrapper,
} from './HomepageHeroWidget.style';
import { Wrapper as HeroWidgetMainBannerWrapper } from 'src/domains/layouts/webview/components/heroEvents/HeroWidgetMainBanner/HeroWidgetMainBanner.style';
import { HeroWidgetModelType } from 'src/api/config/cms_new/hero_widget/get_hero_widget_active';
import { HeroWidgetItemState } from './HeroWidgetItem.state';
import { HeroWidgetBanner } from './HeroWidgetBanner';
import { useCommon } from 'src/domains/common/Common';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { BreakpointsState } from 'src/domains/layouts/state/breakpointsState/BreakpointsState';
import { HeroRaceBoxSelection } from 'src/domains/sportsbook/webview/components/raceBoxSelection/heroWidget/HeroRaceBoxSelection';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { ViewAllButton } from 'src/domains/layouts/webview/components/heroEvents/HeroWidgetMainBanner/ViewAllButton';
import { isEachWayOffered } from 'src/domains/sportsbook/utils/isEachWayOffered';

interface HeroWidgetRacingPropsType {
    heroWidgetItem: HeroWidgetModelType;
}

export const HeroWidgetRacing = observer(
    'HeroWidgetRacing',
    ({ heroWidgetItem }: HeroWidgetRacingPropsType): JSX.Element | null => {
        const common = useCommon();
        const starRouter = StarRouter.get(common);
        const breakpointsState = BreakpointsState.get(common);
        const {
            componentVariants: { sportsbookVariant },
            heroWidgetViewRacecard,
            unifiedSportEventsTable,
        } = ConfigComponents.get(common).unifiedComponentConfig;

        const [heroWidgetItemState] = useState(() => new HeroWidgetItemState(starRouter, heroWidgetItem));
        const { headerImages, promoImages, filteredAndSortDefaultSelections, marketModel, handleRedirectToRacingPage } =
            heroWidgetItemState;
        const { is_active_promo, title, subtitle, id, tc_text, tc_title } = heroWidgetItem;

        const isDesktop = breakpointsState.desktop.isBiggerOrEq;

        if (marketModel === null) {
            return null;
        }

        return (
            <React.Fragment key={marketModel.id}>
                <>
                    <HeroWidgetMainBannerWrapper
                        banners={headerImages}
                        data-test='widget-banner'
                        variant={sportsbookVariant}
                    >
                        <EventWrapper variant={sportsbookVariant}>
                            <EventName data-test='hero-widget-title'>{title}</EventName>
                            <EventInfo data-test='hero-widget-subtitle'>{subtitle}</EventInfo>
                            {isDesktop === false || unifiedSportEventsTable ? (
                                <EventEachWay>
                                    {isEachWayOffered(marketModel) &&
                                    marketModel.eachWayTermsPlaces !== undefined &&
                                    marketModel.eachWayTermsReduction !== undefined
                                        ? `Each way terms: ${marketModel.eachWayTermsReduction} for ${marketModel.eachWayTermsPlaces} Places`
                                        : null}
                                </EventEachWay>
                            ) : null}
                        </EventWrapper>
                        <SpecialEventsHeaderMaxbetText>
                            {isDesktop === true && unifiedSportEventsTable === false ? (
                                <EventEachWay>
                                    {isEachWayOffered(marketModel) &&
                                    marketModel.eachWayTermsPlaces !== undefined &&
                                    marketModel.eachWayTermsReduction !== undefined
                                        ? `Each way terms: ${marketModel.eachWayTermsReduction} for ${marketModel.eachWayTermsPlaces} Places`
                                        : null}
                                </EventEachWay>
                            ) : null}
                        </SpecialEventsHeaderMaxbetText>
                        {unifiedSportEventsTable ? (
                            <ViewAllButton onClick={handleRedirectToRacingPage}>
                                <I18n
                                    langKey='events.racebox.view.label.genius'
                                    defaultText='View Racecard'
                                />
                            </ViewAllButton>
                        ) : null}
                    </HeroWidgetMainBannerWrapper>
                    {
                        <RaceBox variant={sportsbookVariant}>
                            <ListWrapper
                                autoHeightItem
                                variant={sportsbookVariant}
                            >
                                {filteredAndSortDefaultSelections.map((selection) => (
                                    <li key={selection.id}>
                                        <HeroRaceBoxSelection selectionId={selection.id2} />
                                    </li>
                                ))}
                            </ListWrapper>
                            {unifiedSportEventsTable === false && heroWidgetViewRacecard === true ? (
                                <ShowMoreWrapper
                                    onClick={handleRedirectToRacingPage}
                                    variant={sportsbookVariant}
                                    data-test='show-more'
                                >
                                    <I18n
                                        langKey='events.racebox.view.label'
                                        defaultText='View racecard'
                                    />
                                    <ShowMoreArrowWrapper position='right' />
                                </ShowMoreWrapper>
                            ) : null}
                        </RaceBox>
                    }
                </>
                {is_active_promo ? (
                    <HeroWidgetBanner
                        withMargin={false}
                        banners={promoImages}
                        slug={`${id}-hero-promo`}
                        content={tc_text ?? ''}
                        title={tc_title ?? ''}
                    />
                ) : null}
            </React.Fragment>
        );
    }
);
